import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { env } from "@/lib/env";
import { realtime } from "@/lib/realtime";
import { useAuth } from "@/stores/auth";
import { toast } from "@/stores/toast";
import { notificationKeys } from "./api";

const QUERY_HINTS: Record<string, string[][]> = {
  "order.": [["orders"], ["wallet"]],
  "store.order": [
    ["vendor", "orders"],
    ["vendor", "dashboard"],
  ],
  "store.payout": [["wallet"]],
  "store.review": [["vendor", "reviews"]],
  "store.question": [["vendor", "questions"]],
  "store.ads": [["vendor", "ads"]],
  "store.drop": [["vendor", "drops"]],
  "chat.offer": [["chat"], ["cart"]],
  "wallet.": [["wallet"]],
  "rewards.": [["rewards"]],
};

/** How often to re-check chats and notifications while there's no live connection. */
export const POLL_MS = 15_000;

/** Keeps the socket open while signed in and turns server events into fresh data and toasts. */
export function RealtimeBridge() {
  const status = useAuth((s) => s.status);
  const qc = useQueryClient();
  const [connected, setConnected] = useState(realtime.connected);

  useEffect(() => realtime.onStatus(setConnected), []);

  // Fallback when WebSockets are off or down: refresh what's on screen every few seconds (visible tabs only).
  useEffect(() => {
    if (status !== "authenticated" || (env.realtimeEnabled && connected)) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      void qc.invalidateQueries({ queryKey: notificationKeys.unread });
      void qc.invalidateQueries({ queryKey: ["chat"] });
      void qc.invalidateQueries({ queryKey: ["chats", "unread"] });
      if (window.location.pathname.startsWith("/chats")) void qc.invalidateQueries({ queryKey: ["chats", "list"] });
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [status, connected, qc]);

  useEffect(() => {
    if (status !== "authenticated") {
      realtime.stop();
      return;
    }
    realtime.start();
    // Unsent chat messages (outbox) retry after reloads and reconnects; loaded off the first paint.
    void import("@/features/chats/thread").then((m) => m.wireOutbox());
    return undefined;
  }, [status]);

  useEffect(() => {
    const offNote = realtime.on("notification", (data) => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
      const code = String(data.code ?? "");
      Object.entries(QUERY_HINTS).forEach(([prefix, keys]) => {
        if (code.startsWith(prefix)) keys.forEach((key) => void qc.invalidateQueries({ queryKey: key }));
      });
      const link = String(data.link ?? "");
      if (data.id && !(link && window.location.pathname === link.split("?")[0])) {
        toast.info(String(data.title ?? "Notification"), data.body ? String(data.body) : undefined);
      }
    });
    const chat = (data: Record<string, unknown>) => {
      void qc.invalidateQueries({ queryKey: ["chat"] });
      void qc.invalidateQueries({ queryKey: ["chats", "unread"] });
      void qc.invalidateQueries({ queryKey: ["chats", "list"] });
      if (data.conversation) void qc.invalidateQueries({ queryKey: ["chat", "messages", String(data.conversation)] });
    };
    const offs = [
      offNote,
      realtime.on("chat.message", chat),
      realtime.on("chat.read", chat),
      realtime.on("chat.blocked", chat),
      realtime.on("message.new", chat),
    ];
    return () => offs.forEach((off) => off());
  }, [qc]);

  return null;
}
